import { ResolvedMatch, Employee, MeetingAttendee, AttendeePriority } from '../interfaces/interface';
import { MEETING_CONSTANTS } from '../constants/EAHuddleConstants';
import { EmployeeService } from '../services/EmployeeService';

/**
 * Compute the denormalized set of logins authorized to access a meeting.
 * Includes: creator, organizer, onBehalfOf, all attendee logins, and all EA logins.
 */
export const computeAuthorizedLogins = (
  creatorAlias: string,
  attendees: MeetingAttendee[],
  options?: { organizerLogin?: string; onBehalfOfLogin?: string | null }
): string[] => {
  const logins = new Set<string>();
  logins.add(creatorAlias.toLowerCase());
  if (options?.organizerLogin) logins.add(options.organizerLogin.toLowerCase());
  if (options?.onBehalfOfLogin) logins.add(options.onBehalfOfLogin.toLowerCase());
  for (const a of attendees) {
    if (a.loginName) logins.add(a.loginName.toLowerCase());
    if (a.eaLogin) logins.add(a.eaLogin.toLowerCase());
  }
  return Array.from(logins);
};

/**
 * Build MeetingAttendee list from resolved attendees, including eaLogin.
 */
export const buildMeetingAttendees = (resolvedAttendees: ResolvedMatch[]): MeetingAttendee[] => {
  return resolvedAttendees
    .filter((a) => a.selectedEmployee?.login_name)
    .map((a) => ({
      loginName: a.selectedEmployee!.login_name,
      priority: a.priority,
      eaLogin: a.selectedExecutiveAssistant?.login_name || undefined,
      employeeName: a.selectedEmployee!.employee_name || undefined,
      proxyForLogin: a.proxyForLogin || undefined,
      proxyForName: a.proxyForName || undefined
    }));
};

/**
 * Normalizes a login name to lowercase for consistent comparison.
 */
export const normalizeLoginName = (employee: Employee): string => {
  return employee.login_name?.toLowerCase() ?? '';
};

/**
 * Checks if an employee already exists in the attendees list based on login name.
 */
export const isDuplicateEmployee = (employee: Employee, currentAttendees: ResolvedMatch[]): boolean => {
  const loginName = normalizeLoginName(employee);
  if (!loginName) return false;

  return currentAttendees.some((attendee) => {
    const existingLoginName = attendee.selectedEmployee?.login_name?.toLowerCase();
    return existingLoginName === loginName;
  });
};

/**
 * Sorts attendees by the following criteria:
 * 1. Unresolved attendees first (need attention)
 * 2. Job level (higher levels first - L8 before L6)
 * 3. Priority (required before optional)
 * 4. Alphabetical by name
 */
export const sortAttendees = (attendees: ResolvedMatch[]): ResolvedMatch[] => {
  const principals = attendees.filter((a) => !a.proxyForLogin);
  const proxies = attendees.filter((a) => a.proxyForLogin);

  // Sort principals normally
  const sorted = [...principals].sort((a, b) => {
    const aResolved = a.selectedEmployee;
    const bResolved = b.selectedEmployee;

    // Unresolved attendees first (need attention)
    if (!aResolved && bResolved) return -1;
    if (!bResolved && aResolved) return 1;

    // If both are unresolved, maintain original order
    if (!aResolved && !bResolved) return 0;

    // Both are resolved - apply sorting logic
    if (aResolved && bResolved) {
      // Priority first (critical → required → optional)
      const priorityOrder: Record<string, number> = { critical: 0, required: 1, optional: 2 };
      if (a.priority !== b.priority) {
        return priorityOrder[a.priority] - priorityOrder[b.priority];
      }

      // Then job level within the same priority group (higher levels first)
      const aLevel = parseInt(a.selectedEmployee?.job_level_name || '0');
      const bLevel = parseInt(b.selectedEmployee?.job_level_name || '0');
      if (aLevel !== bLevel) return bLevel - aLevel;

      // Alphabetical by name
      const aName = a.selectedEmployee?.employee_name || a.inputName;
      const bName = b.selectedEmployee?.employee_name || b.inputName;
      return aName.localeCompare(bName);
    }

    return 0;
  });

  // Insert proxies immediately after their principal
  const result: ResolvedMatch[] = [];
  for (const principal of sorted) {
    result.push(principal);
    const login = principal.selectedEmployee?.login_name?.toLowerCase();
    if (login) {
      const matchingProxies = proxies.filter((p) => p.proxyForLogin?.toLowerCase() === login);
      result.push(...matchingProxies);
    }
  }

  // Append any orphaned proxies (principal was removed)
  const placed = new Set(result.map((a) => a.id));
  for (const proxy of proxies) {
    if (!placed.has(proxy.id)) result.push(proxy);
  }

  return result;
};

/**
 * Finds duplicate email addresses in the attendees list.
 */
export const findDuplicateEmails = (attendees: ResolvedMatch[]): Set<string> => {
  const emailCounts = new Map<string, number>();
  const duplicates = new Set<string>();

  attendees.forEach((attendee) => {
    const email = attendee.resolvedEmail?.toLowerCase();
    if (email) {
      const count = emailCounts.get(email) || 0;
      emailCounts.set(email, count + 1);
      if (count >= 1) {
        duplicates.add(email);
      }
    }
  });

  return duplicates;
};

/**
 * Gets all existing email addresses from attendees.
 */
export const getExistingEmails = (attendees: ResolvedMatch[]): Set<string> => {
  return new Set(attendees.map((a) => a.resolvedEmail?.toLowerCase()).filter(Boolean) as string[]);
};

/**
 * Checks if there are any unresolved attendee names.
 */
export const hasUnresolvedNames = (attendees: ResolvedMatch[]): boolean => {
  return attendees.some((attendee) => !attendee.selectedEmployee);
};

/**
 * Load attendees in rate-limited batches to stay under the PAPI 50 TPS limit.
 *
 * Attendees with a valid browser cache entry are loaded immediately (no delay),
 * since they won't hit PAPI. Only cache misses go through rate-limited batching.
 *
 * Each `addAttendee` call triggers ~2 PAPI requests (employee lookup + EA lookup).
 * With batch size 5 that's ~10 PAPI calls/sec/user — well within limits.
 *
 * @param attendeeList - The attendees to load
 * @param addAttendee - Callback that triggers the actual employee resolution
 * @param onProgress - Optional progress callback (e.g. for UI feedback)
 */
export const loadAttendeesInBatches = async (
  attendeeList: MeetingAttendee[],
  addAttendee: (
    employee: Employee,
    priority: AttendeePriority,
    extraFields?: { proxyForLogin?: string; proxyForName?: string }
  ) => Promise<void> | void,
  onProgress?: (message: string) => void
): Promise<void> => {
  const { ATTENDEE_BATCH_SIZE, BATCH_DELAY_MS } = MEETING_CONSTANTS;
  const total = attendeeList.length;

  // Split into cache hits (instant) and misses (need rate-limited PAPI calls)
  const cached: MeetingAttendee[] = [];
  const uncached: MeetingAttendee[] = [];
  for (const attendee of attendeeList) {
    if (EmployeeService.hasBrowserCache(attendee.loginName)) {
      cached.push(attendee);
    } else {
      uncached.push(attendee);
    }
  }

  const toEmployee = (attendee: MeetingAttendee): Employee => ({
    login_name: attendee.loginName,
    employee_name: '',
    employee_internal_email_address: '',
    job_title_name: '',
    location_city_name: '',
    location_state_name: '',
    job_level_name: '',
    department_name_current: ''
  });

  const toExtraFields = (attendee: MeetingAttendee) =>
    attendee.proxyForLogin ? { proxyForLogin: attendee.proxyForLogin, proxyForName: attendee.proxyForName } : undefined;

  // Load cache hits immediately — no PAPI calls, no delay needed
  if (cached.length > 0) {
    onProgress?.(`Loading attendees (${cached.length}/${total}) from cache...`);
    await Promise.all(cached.map((a) => addAttendee(toEmployee(a), a.priority, toExtraFields(a))));
  }

  // Rate-limit only the cache misses
  for (let i = 0; i < uncached.length; i += ATTENDEE_BATCH_SIZE) {
    const batch = uncached.slice(i, i + ATTENDEE_BATCH_SIZE);
    const loaded = cached.length + Math.min(i + ATTENDEE_BATCH_SIZE, uncached.length);
    onProgress?.(`Loading attendees (${loaded}/${total})...`);

    await Promise.all(batch.map((a) => addAttendee(toEmployee(a), a.priority, toExtraFields(a))));

    // Delay between batches to respect rate limits
    if (i + ATTENDEE_BATCH_SIZE < uncached.length) {
      await new Promise((resolve) => setTimeout(resolve, BATCH_DELAY_MS));
    }
  }
};

// Re-export from shared location for backward compatibility
export { generateMultiBadgerUrl } from '../../shared/urlUtils';
